import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import {
  ASIGNAR_RETORNABLES,
  DEVOLVER_RETORNABLES,
  RETORNABLES_MOVIMIENTOS_QUERY,
  RETORNABLES_SALDOS_QUERY,
  RETORNABLES_TIPOS_QUERY,
} from '@/graphql/retornables.gql.js';
import { useAuth } from '@/state/auth/AuthContext.jsx';

export default function DocumentacionPorBolsa() {
  const { sucursal } = useAuth();
  const [searchParams] = useSearchParams();
  const sucursalId = Number(sucursal?.id || 1);
  const [contexto, setContexto] = useState(searchParams.get('contexto') || 'guia');
  const [contextoId, setContextoId] = useState(searchParams.get('id') || '');
  const [clienteId, setClienteId] = useState(searchParams.get('clienteId') || '');
  const [tipoId, setTipoId] = useState('');
  const [cantidad, setCantidad] = useState(1);
  const [estadoDevolucion, setEstadoDevolucion] = useState('ok');
  const [detalle, setDetalle] = useState('');
  const [evidenciaJson, setEvidenciaJson] = useState('');

  const filtro = useMemo(() => ({
    sucursalId,
    clienteId: clienteId ? Number(clienteId) : null,
    ...(contexto === 'guia' && contextoId ? { guiaId: Number(contextoId) } : {}),
    ...(contexto === 'foja' && contextoId ? { fojaId: Number(contextoId) } : {}),
    ...(contexto === 'reparto' && contextoId ? { repartoId: Number(contextoId) } : {}),
  }), [clienteId, contexto, contextoId, sucursalId]);

  const [tiposResult] = useQuery({ query: RETORNABLES_TIPOS_QUERY, variables: { activo: true } });
  const [movResult, reexecMov] = useQuery({ query: RETORNABLES_MOVIMIENTOS_QUERY, variables: { filtro } });
  const [saldosResult] = useQuery({
    query: RETORNABLES_SALDOS_QUERY,
    variables: { sucursalId, clienteId: clienteId ? Number(clienteId) : null },
  });
  const [, asignar] = useMutation(ASIGNAR_RETORNABLES);
  const [, devolver] = useMutation(DEVOLVER_RETORNABLES);

  const contextField = `${contexto}Id`;
  const movimientos = useMemo(() => movResult.data?.retornablesMovimientos || [], [movResult.data]);
  const saldos = useMemo(() => saldosResult.data?.retornablesSaldos || [], [saldosResult.data]);
  const tipos = tiposResult.data?.retornablesTipos || [];
  const tiposMap = useMemo(
    () => new Map(tipos.map((item) => [Number(item.id), item.nombre])),
    [tipos],
  );

  const payloadBase = {
    sucursalId,
    tipoId: Number(tipoId),
    cantidad: Number(cantidad || 1),
    clienteId: clienteId ? Number(clienteId) : null,
    detalle,
    evidenciaJson: evidenciaJson || null,
    [contextField]: contextoId ? Number(contextoId) : null,
  };

  const handleAsignar = async () => {
    if (!tipoId) return;
    await asignar({ input: payloadBase });
    reexecMov({ requestPolicy: 'network-only' });
  };

  const handleDevolver = async () => {
    if (!tipoId) return;
    await devolver({ input: { ...payloadBase, estado: estadoDevolucion } });
    reexecMov({ requestPolicy: 'network-only' });
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-4 px-4 py-4">
      <h1 className="text-2xl font-semibold">Documentación por Bolsa</h1>
      <div className="grid grid-cols-12 gap-4">
        <section className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgb(var(--panel))] p-4">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12 md:col-span-2">
              <label className="text-sm">Contexto</label>
              <select className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2" value={contexto} onChange={e => setContexto(e.target.value)}>
                <option value="guia">Guía</option>
                <option value="foja">Foja</option>
                <option value="reparto">Reparto</option>
              </select>
            </div>
            <div className="col-span-12 md:col-span-2">
              <label className="text-sm">ID contexto</label>
              <input className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2" value={contextoId} onChange={e => setContextoId(e.target.value)} />
            </div>
            <div className="col-span-12 md:col-span-2">
              <label className="text-sm">Cliente ID</label>
              <input className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2" value={clienteId} onChange={e => setClienteId(e.target.value)} />
            </div>
            <div className="col-span-12 md:col-span-3">
              <label className="text-sm">Tipo envase</label>
              <select className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2" value={tipoId} onChange={e => setTipoId(e.target.value)}>
                <option value="">Seleccionar</option>
                {tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
              </select>
            </div>
            <div className="col-span-12 md:col-span-1">
              <label className="text-sm">Cantidad</label>
              <input type="number" min="1" className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2" value={cantidad} onChange={e => setCantidad(e.target.value)} />
            </div>
            <div className="col-span-12 md:col-span-2">
              <label className="text-sm">Estado devolución</label>
              <select className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2" value={estadoDevolucion} onChange={e => setEstadoDevolucion(e.target.value)}>
                <option value="ok">OK</option>
                <option value="faltante">Faltante</option>
                <option value="danado">Dañado</option>
              </select>
            </div>
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm">Detalle incidencia</label>
              <input className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2" value={detalle} onChange={e => setDetalle(e.target.value)} />
            </div>
            <div className="col-span-12 md:col-span-6">
              <label className="text-sm">Evidencia (JSON / URL foto)</label>
              <input className="mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-transparent px-3 py-2" value={evidenciaJson} onChange={e => setEvidenciaJson(e.target.value)} />
            </div>
          </div>
          <div className="sticky bottom-0 mt-4 flex flex-wrap justify-end gap-2 border-t border-[rgba(var(--border),0.5)] bg-[rgb(var(--panel))] py-3">
            <button className="rounded-xl border border-[rgba(var(--border),0.6)] px-4 py-2" onClick={handleAsignar}>Asignar</button>
            <button className="rounded-xl border border-[rgba(var(--border),0.6)] px-4 py-2" onClick={handleDevolver}>Marcar devolución / incidencia</button>
          </div>
        </section>

        <section className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgb(var(--panel))] p-4">
          <h2 className="mb-2 font-semibold">Saldos abiertos</h2>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead><tr><th className="text-left">Tipo</th><th>Cliente</th><th className="text-right">Saldo</th></tr></thead>
              <tbody>
                {saldos.length ? saldos.map((row, index) => (
                  <tr key={`${row.tipoId}-${row.clienteId || 'sin-cliente'}-${index}`} className="border-t border-[rgba(var(--border),0.4)]">
                    <td>{tiposMap.get(Number(row.tipoId)) || `Tipo #${row.tipoId}`}</td>
                    <td className="text-center">{row.clienteId || '-'}</td>
                    <td className="text-right font-semibold">{row.saldo}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={3} className="py-3 text-center text-[rgba(var(--fg),0.6)]">Sin saldos para el filtro actual.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.45)] bg-[rgb(var(--panel))] p-4">
          <h2 className="mb-2 font-semibold">Historial</h2>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead><tr><th className="text-left">Fecha</th><th>Tipo</th><th>Movimiento</th><th>Guía/Foja/Reparto</th><th>Cliente</th><th>Cant.</th><th>Estado</th><th>Detalle</th></tr></thead>
              <tbody>
                {movimientos.map(m => (
                  <tr key={m.id} className="border-t border-[rgba(var(--border),0.4)]">
                    <td>{new Date(m.fecha).toLocaleString('es-AR')}</td>
                    <td>{tiposMap.get(Number(m.tipoId)) || `Tipo #${m.tipoId}`}</td>
                    <td>{m.movimiento}</td>
                    <td>{m.guiaId ? `Guía #${m.guiaId}` : m.fojaId ? `Foja #${m.fojaId}` : m.repartoId ? `Reparto #${m.repartoId}` : '-'}</td>
                    <td>{m.clienteId || '-'}</td>
                    <td>{m.cantidad}</td>
                    <td><span className="rounded-full border border-[rgba(var(--border),0.6)] px-2 py-1">{m.estado}</span></td>
                    <td>{m.detalle || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
